import {createAsyncThunk, createSlice, PayloadAction} from "@reduxjs/toolkit";
import axios from "axios";
import {RootState} from "../../../../../dist";
import {NotificationStatus} from "./notificationEnums";
import {NotificationDto, NotificationDetailsDto, RequestNotificationDetailsDto} from "./notificationInterfaces";
import {getFoundationServiceSchemeAndHost} from "../../../utils/getFoundationServiceSchemeAndHost";
import {NotificationEvent} from "../../../types/NotificationEvent";
import {NotificationReadEvent} from "../../../types/NotificationReadEvent";
import {receiveAxiosError} from "../../errors/data/errorSlice";

export interface InitialState {
  notifications: NotificationDto[];
  notificationDetails: NotificationDetailsDto[];
  page: number;
  sizePerPage: number;
  totalUnread: number;
  isLoading: boolean;
  isLoaded: boolean;
  isLoadingDetails: boolean;
  totalSize: number;
  isError: boolean;
}

export const initialState: InitialState = {
  notifications: [],
  notificationDetails: [],
  page: 1,
  sizePerPage: 10,
  totalUnread: 0,
  isLoading: false,
  isLoaded: false,
  isLoadingDetails: false,
  totalSize: 0,
  isError: false,
};

export const requestNotifications = createAsyncThunk("notification/requestNotifications", async (_params, thunkApi) => {
  const {dispatch} = thunkApi;
  try {
    const query = `${getFoundationServiceSchemeAndHost()}/notification/api/notifications`;

    const response = await axios.get<NotificationDto[]>(query);

    return response.data;
  } catch (error) {
    dispatch(receiveAxiosError({error}));
    return thunkApi.rejectWithValue(error);
  }
});

export const requestNotificationDetails = createAsyncThunk(
  "notification/requestNotificationDetails",
  async (_params, thunkApi) => {
    const {getState, dispatch} = thunkApi;
    const appState = getState() as RootState;

    // Get our current account Ids
    let notificationIds = appState.notification.notifications.slice(
      (appState.notification.page - 1) * appState.notification.sizePerPage,
      (appState.notification.page + 1) * appState.notification.sizePerPage
    );

    // Select the IDs we don't have data for
    let loadNotificationIds = notificationIds.filter(
      (x: NotificationDto) =>
        !appState.notification.notificationDetails.some((y: NotificationDetailsDto) => y.id === x.id)
    );

    if (loadNotificationIds.length === 0) {
      return;
    }

    let request = {} as RequestNotificationDetailsDto;
    request.notificationIds = loadNotificationIds.map((cache: NotificationDto) => {
      return cache.id;
    });

    const url = `${getFoundationServiceSchemeAndHost()}/notification/api/notifications`;

    try {
      const response = await axios.post<NotificationDetailsDto[]>(url, request);
      return response.data;
    } catch (error) {
      dispatch(receiveAxiosError({error}));
      return thunkApi.rejectWithValue(error);
    }
  }
);

export const requestReadNotifications = createAsyncThunk(
  "notification/requestReadNotifications",
  async (_params, thunkApi) => {
    const {getState, dispatch} = thunkApi;
    const appState = getState() as RootState;

    // Get our current account Ids
    const notificationIds = appState.notification.notifications
      .filter((x: NotificationDto) => !x.isRead)
      .map((x: NotificationDto) => x.id);

    if (notificationIds.length === 0) {
      return [];
    }

    const request = {} as RequestNotificationDetailsDto;
    request.notificationIds = notificationIds;

    const url = `${getFoundationServiceSchemeAndHost()}/notification/api/notifications`;

    try {
      await axios.put(url, request);
      return notificationIds;
    } catch (error) {
      dispatch(receiveAxiosError({error}));
      return thunkApi.rejectWithValue(error);
    }
  }
);

export const notificationSlice = createSlice({
  name: "notification",
  initialState,
  reducers: {
    receiveNotification(state, action: PayloadAction<NotificationEvent>) {
      // Take user back to page 1 if searchText or activeContracts are updated
      let dto = {} as NotificationDetailsDto;
      dto.id = action.payload.id;
      dto.createdDate = action.payload.createdDate;
      dto.title = action.payload.title;
      dto.message = action.payload.message;
      dto.status = action.payload.status;
      dto.subjectDisplayName = action.payload.subjectDisplayName;
      dto.subjectId = action.payload.subjectId;
      dto.subjectVersion = action.payload.subjectVersion;
      dto.subjectLink = action.payload.subjectLink;
      dto.subjectType = action.payload.subjectType;
      dto.supersedeEarlier = action.payload.supersedeEarlier;

      switch (action.payload.status) {
        case 0:
          dto.status = NotificationStatus.Info;
          break;
        case 1:
          dto.status = NotificationStatus.Pending;
          break;
        case 2:
          dto.status = NotificationStatus.Success;
          break;
        case 3:
          dto.status = NotificationStatus.Warning;
          break;
        case 4:
          dto.status = NotificationStatus.Error;
          break;
      }

      if (action.payload.supersedeEarlier) {
        state.notifications = state.notifications.filter(
          (x) => x.subjectId !== action.payload.subjectId || x.subjectType !== action.payload.subjectType
        );
      }

      let notificationDto = {} as NotificationDto;
      notificationDto.id = action.payload.id;
      notificationDto.isRead = false;
      notificationDto.subjectId = action.payload.subjectId;
      notificationDto.subjectType = action.payload.subjectType;

      state.notificationDetails.push(dto);
      state.notifications = [notificationDto].concat(state.notifications);
      state.totalUnread = state.notifications.filter((x: NotificationDto) => !x.isRead).length;
    },
    receiveReadNotification(state, action: PayloadAction<NotificationReadEvent>) {
      let index = state.notifications.findIndex((x: NotificationDto) => x.id == action.payload.notificationId);

      if (index < 0) return;

      state.notifications[index].isRead = true;
      state.totalUnread = state.notifications.filter((x: NotificationDto) => !x.isRead).length;
    },
    updateNotificationPage(state, action: PayloadAction<number>) {
      if (state.page !== action.payload) {
        state.page = action.payload;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(requestNotifications.pending, (state) => {
        state.isLoading = true;
        state.isLoaded = false;
      })
      .addCase(requestNotifications.fulfilled, (state, action) => {
        state.notifications = action.payload;
        state.totalSize = action.payload.length;
        state.isLoading = false;
        state.isLoaded = true;
        state.totalUnread = state.notifications.filter((x: NotificationDto) => !x.isRead).length;
      })
      .addCase(requestNotifications.rejected, (state) => {
        state.notifications = [];
        state.totalSize = 0;
        state.isLoading = false;
        state.isLoaded = true;
        state.isError = true;
      })
      .addCase(requestNotificationDetails.pending, (state) => {
        state.isLoadingDetails = true;
      })
      .addCase(requestNotificationDetails.fulfilled, (state, action) => {
        if (action.payload !== undefined) {
          action.payload.map((detail) => {
            state.notificationDetails.push(detail);
          });
        }

        state.isLoadingDetails = false;
      })
      .addCase(requestNotificationDetails.rejected, (state) => {
        state.isLoadingDetails = false;
        state.isError = true;
      })
      .addCase(requestReadNotifications.pending, (state) => {
        state.isLoading = true;
        state.isLoaded = false;
      })
      .addCase(requestReadNotifications.fulfilled, (state, action) => {
        action.payload.map((id: string) => {
          let readNotification = state.notifications.find((x) => x.id === id);

          if (readNotification !== undefined) readNotification.isRead = true;
        });

        state.totalUnread = state.notifications.filter((x: NotificationDto) => !x.isRead).length;
        state.isLoading = false;
        state.isLoaded = true;
      })
      .addCase(requestReadNotifications.rejected, (state, _action) => {
        state.isLoading = false;
        state.isLoaded = true;
        state.isError = true;
      });
  },
});

export const {receiveNotification, receiveReadNotification, updateNotificationPage} = notificationSlice.actions;
export default notificationSlice;
